import React, { useState, KeyboardEvent, useEffect, useRef } from 'react';
import {
  Button,
  Select,
  Checkbox,
  Form,
  Row,
  Col,
  Skeleton,
  Space,
  Popover,
  Input,
  Tag,
  Segmented,
  Dropdown
} from 'antd';
import Prompt from 'assets/prompt.svg';
import close from 'assets/close.svg';
import { useChatState } from '../state/chatState';
import { Metadata, SessionFile } from 'api/useChat';
import FileUploadDialog from '../FileUploadModal/FileUploadModal';
import { CheckCircleFilled, FileFilled, FileTextFilled, PaperClipOutlined, PlusCircleFilled } from '@ant-design/icons';
import './PromptArea.scss';
import { SegmentedValue } from 'antd/es/segmented';
import { MenuProps } from 'antd/es/menu';
import { Mode } from '../state/chatState';

const DEAL_TYPE_OPTIONS = [
  { value: 'Renewal', label: 'Renewal' },
  { value: 'New purchase', label: 'New purchase' },
  { value: 'Add-on purchase', label: 'Add-on purchase' }
];
const PROMPT_MODES_OPTIONS = [
  { value: 'Ask', label: 'CHAT' },
  { value: 'Research', label: 'DEEP RESEARCH' },
  { value: 'writer', label: 'REPORT WRITER' }
];
const WRITER_MODES_OPTIONS_MENU: MenuProps['items'] = [
  //{ key: 'Health Check', label: 'HEALTH CHECK', icon: <PlusCircleFilled /> },
  { key: 'Quick Check', label: 'QUICK CHECK', icon: <FileFilled /> }
  //{ key: 'Full FMV Report', label: 'FULL FMV REPORT', icon: <FileTextFilled /> }
];
type DealType = 'Renewal' | 'New purchase' | 'Add-on purchase';
const PROMPT_OVERLAY_Z_INDEX = 2147483647;

/** Same stacking order as `FileUploadModal` / deal-header `Drawer` so overlays stay on top when chat is embedded. */
const CHAT_PROMPT_OVERLAY_Z_INDEX = 999999999999;
interface MessageData {
  vendor: string | undefined;
  dealType: DealType | undefined;
}

const PromptArea: React.FC<{ mode: Mode }> = ({ mode }) => {
  const [isDialogVisible, setIsDialogVisible] = useState({ source: 'prompt', isOpen: false });

  const [selectedFiles, setSelectedFiles] = useState<SessionFile[]>([]);
  const [quickCheckFiles, setQuickCheckFiles] = useState<SessionFile[]>([]);

  const openDialog = (source?: string) => {
    setIsDialogVisible({ source: source || 'prompt', isOpen: true });
  };

  const closeDialog = (source?: string) => {
    setIsDialogVisible({ source: source || 'prompt', isOpen: false });
  };

  const handleFilesSelected = (files: SessionFile[]) => {
    if (isDialogVisible.source === 'quickCheck') {
      setQuickCheckFiles(files);
    } else {
      setSelectedFiles(files);
    }
    setIsDialogVisible({ source: isDialogVisible.source, isOpen: false });
  };

  const [formState, setFormState] = useState({
    inputValue: '',
    vendor: undefined as string | undefined,
    dealType: undefined as DealType | undefined
  });

  const [isDocumentWriterOpen, setIsDocumentWriterOpen] = useState(false);

  const {
    state: { isLoading, currentSessionId, promptMode, promptAreaData, reports, embeddedWindowState, projects },
    sendMessage,
    stopStreaming,
    setIsDocumentMode,
    setPromptMode,
    setReport
  } = useChatState();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (currentSessionId?.metadata) {
      const { vendor, dealType } = currentSessionId.metadata;
      setFormState((prev) => ({
        ...prev,
        vendor: vendor,
        dealType: dealType
      }));
    } else {
      setFormState((prev) => ({
        ...prev,
        inputValue: '',
        vendor: undefined,
        dealType: undefined
      }));
    }
    if (!isLoading.isStreaming && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [currentSessionId, isLoading.isStreaming]);

  const isDisabled = isLoading.isStreaming;

  const handleSupplierChange = React.useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, vendor: value }));
  }, []);

  const handleDealTypeChange = React.useCallback((value: string | null) => {
    setFormState((prev) => ({ ...prev, dealType: (value as DealType) || undefined }));
  }, []);

  const handleModeChange = React.useCallback((value: SegmentedValue) => {
    switch (value) {
      case 'Research':
        setPromptMode(value);
        setIsDocumentMode(true);
        setReport(reports[value] || '');

        break;
      case 'Ask':
        setPromptMode(value);
        break;
      case 'writer':
        setIsDocumentWriterOpen(true);
        break;
    }
  }, []);
  const handleDocumentWriterSelect = React.useCallback((key: string) => {
    setPromptMode(key);
    setIsDocumentMode(true);
    setIsDocumentWriterOpen(false);
    setReport(reports[key] || '');
  }, []);

  const handleSendMessage = React.useCallback(() => {
    if (formState.inputValue.trim() && !isDisabled) {
      const messageData: MessageData = {
        vendor: formState.vendor,
        dealType: formState.dealType
      };

      sendMessage(formState.inputValue.trim(), promptMode || '', messageData as Metadata, selectedFiles);
      setFormState((prev) => ({ ...prev, inputValue: '' }));
      setSelectedFiles([]);
    }
  }, [formState, isDisabled, sendMessage, selectedFiles, promptMode]);

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileClose = (fileId: string, source: string) => {
    if (source === 'prompt') {
      setSelectedFiles(selectedFiles.filter((f) => f.id !== fileId));
    } else {
      setQuickCheckFiles(quickCheckFiles.filter((f) => f.id !== fileId));
    }
  };

  const renderSelectedFiles = (selectedFiles: SessionFile[], source: string) => {
    return (
      <div>
        {selectedFiles.map((file: SessionFile) => (
          <Tag
            key={file.id}
            closable
            onClose={() => handleFileClose(file.id, source)}
            className="mr-2 mb-1 p-1 font-bold leading-5 rounded-lg inline-flex items-center"
            title={file.filename}
          >
            <span className="max-w-[200px] truncate block">{file.filename}</span>
          </Tag>
        ))}
      </div>
    );
  };

  return (
    <div className="relative z-10 min-h-[148px] max-h-[250px]">
      {/* File Upload Dialog */}
      <FileUploadDialog
        visible={isDialogVisible.isOpen}
        onCancel={closeDialog}
        onOk={handleFilesSelected}
        projectId={currentSessionId?.project?.id || projects?.GENERAL?.[0]?.id}
        source={isDialogVisible.source}
      />

      <Form layout="horizontal" className="mb-2">
        <Row className="gap-1 items-center">
          <Col flex={1} className="overflow-x-auto">
            <Space>
              {PROMPT_MODES_OPTIONS.map((option) =>
                option.value === 'writer' ? (
                  <Dropdown
                    menu={{ items: WRITER_MODES_OPTIONS_MENU, onClick: ({ key }) => handleDocumentWriterSelect(key) }}
                    key={option.value}
                    placement="topRight"
                    trigger={['click']}
                    open={isDocumentWriterOpen}
                    getPopupContainer={() => document.body}
                    overlayStyle={{ zIndex: PROMPT_OVERLAY_Z_INDEX }}
                    overlayClassName="prompt-writer-dropdown"
                    disabled={
                      isDisabled ||
                      (mode !== 'horizontal' && embeddedWindowState.isEmbeddedWindowOpen) ||
                      currentSessionId?.category !== 'DEAL'
                    }
                  >
                    <Button
                      size="small"
                      //if promptMode is option.value, then add active class
                      className={
                        WRITER_MODES_OPTIONS_MENU.find((menu) => menu?.key === promptMode)
                          ? 'prompt-mode-button active'
                          : 'prompt-mode-button'
                      }
                      key={option.value}
                      onClick={() => handleModeChange(option.value)}
                    >
                      {WRITER_MODES_OPTIONS_MENU.find((menu) => menu?.key === promptMode) ? promptMode : option.label}
                    </Button>
                  </Dropdown>
                ) : (
                  <Button
                    size="small"
                    className={promptMode === option.value ? 'prompt-mode-button active' : 'prompt-mode-button'}
                    key={option.value}
                    disabled={
                      option.value == 'Ask'
                        ? isDisabled
                        : isDisabled || (mode !== 'horizontal' && embeddedWindowState.isEmbeddedWindowOpen)
                    }
                    onClick={() => handleModeChange(option.value)}
                  >
                    {option.label}
                  </Button>
                )
              )}
            </Space>
          </Col>

          {(!currentSessionId ||
            (currentSessionId && currentSessionId.category !== 'DEAL' && !currentSessionId.id)) && (
            <Col flex={1}>
              <div className="grid grid-cols-2 gap-2">
                <Form.Item className="mb-0 ">
                  <Select
                    className="text-ellipsis  truncate "
                    placeholder="Select Supplier (optional)"
                    value={formState.vendor}
                    onChange={handleSupplierChange}
                    options={promptAreaData.vendor.map((option: { value: string; label: string }) => ({
                      value: option.value,
                      label:
                        typeof option.label === 'string'
                          ? option.label.charAt(0).toUpperCase() + option.label.slice(1).toLowerCase()
                          : option.label
                    }))}
                    disabled={isDisabled}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    dropdownMatchSelectWidth={false}
                    filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                    }
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: PROMPT_OVERLAY_Z_INDEX }}
                  />
                </Form.Item>
                <Form.Item className="mb-0 ">
                  <Select
                    className="text-ellipsis  truncate "
                    placeholder="Select Deal Type (optional)"
                    value={formState.dealType}
                    onChange={handleDealTypeChange}
                    options={DEAL_TYPE_OPTIONS}
                    disabled={isDisabled}
                    allowClear
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={{ zIndex: CHAT_PROMPT_OVERLAY_Z_INDEX }}
                  />
                </Form.Item>
              </div>
            </Col>
          )}
        </Row>
      </Form>
      <div className=" border border-solid p-2 rounded-lg border-gray-200 bg-white">
        <div className="flex  items-center">
          <Input.TextArea
            ref={textAreaRef}
            placeholder="Type your prompt here..."
            value={formState.inputValue}
            onChange={(e) => setFormState((prev) => ({ ...prev, inputValue: e.target.value }))}
            onKeyDown={handleKeyPress}
            disabled={isDisabled}
            className="flex-1 mr-2"
            autoSize={{ minRows: 3, maxRows: 6 }}
            bordered={false}
            style={{ resize: 'none', fontSize: '14px' }}
            autoFocus={!isDisabled}
            id="chat-input-textarea"
          />
          <Button
            type="link"
            icon={
              isDisabled ? (
                <img src={close} alt="close" width={24} height={24} />
              ) : (
                <img src={Prompt} alt="Prompt" width={24} height={24} />
              )
            }
            onClick={isDisabled ? stopStreaming : () => handleSendMessage()}
            disabled={!isDisabled && !formState.inputValue.trim()}
          ></Button>
        </div>

        {
          <div>
            {renderSelectedFiles(selectedFiles, 'prompt')}
            <Button disabled={isDisabled} type="link" icon={<PaperClipOutlined />} onClick={() => openDialog('Prompt')}>
              Upload Files/Select Files
            </Button>
          </div>
        }
      </div>
    </div>
  );
};

export default PromptArea;
